import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import type { Language } from '@/types';
import { fetchQCReports, type QCReportRow } from '../api';

interface QCWorkspaceProps {
  language: Language;
  onSelectSite: (siteId: string) => void;
}

type SiteListing = {
  id: string;
  name: string;
  address: string;
  customer_number: string | null;
  active: boolean;
};

type SortKey = 'recent_qc' | 'oldest_qc' | 'no_qc' | 'name_asc';

type SiteLastCleaning = {
  siteId: string;
  scheduledFor: string;
};

const copy = {
  en: {
    title: 'KVALITETSKONTROLL — OBJEKT',
    search: 'Search BRF, address or customer number',
    sortRecent: 'RECENT QC FIRST',
    sortOldest: 'OLDEST QC FIRST',
    sortNoQc: 'NEVER QCD FIRST',
    sortName: 'NAME (A–Z)',
    colBrf: 'BRF',
    colLastQc: 'LAST QC',
    colScore: 'SCORE',
    colLastCleaning: 'LAST CLEANING',
    loading: 'Loading sites…',
    empty: 'No BRFs match.',
    none: '—',
    failed: 'UNDERKÄND',
    objects: 'OBJECTS',
  },
  sv: {
    title: 'KVALITETSKONTROLL — OBJEKT',
    search: 'Sök BRF, adress eller kundnr',
    sortRecent: 'SENAST QC FÖRST',
    sortOldest: 'ÄLDSTA QC FÖRST',
    sortNoQc: 'ALDRIG QC FÖRST',
    sortName: 'NAMN (A–Ö)',
    colBrf: 'BRF',
    colLastQc: 'SENASTE QC',
    colScore: 'POÄNG',
    colLastCleaning: 'SENASTE STÄDNING',
    loading: 'Laddar BRF:er…',
    empty: 'Inga BRF:er matchar.',
    none: '—',
    failed: 'UNDERKÄND',
    objects: 'OBJEKT',
  },
} as const;

const OK_INK = '#1B7F4D';
const FAIL_INK = '#C24138';

function ScoreNumeral({ score }: { score: 1 | 2 | 3 | 4 | 5 | null }) {
  if (score == null) {
    return <span className="font-mono tabular-nums text-xs text-[#607C6E]">—</span>;
  }
  // 1–2 reads as a failing inspection, 3+ passing.
  const ink = score >= 3 ? OK_INK : FAIL_INK;
  return (
    <span className="font-mono tabular-nums text-xs" style={{ color: ink }}>
      {score}<span className="text-[#607C6E]">/5</span>
    </span>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

export function QCWorkspace({ language, onSelectSite }: QCWorkspaceProps) {
  const text = copy[language];
  const { session } = useAuth();
  const token = session?.access_token ?? null;

  const [sites, setSites] = useState<SiteListing[]>([]);
  const [reports, setReports] = useState<QCReportRow[]>([]);
  const [lastCleanings, setLastCleanings] = useState<SiteLastCleaning[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('recent_qc');

  const loadAll = async () => {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const [sitesRes, jobsRes, qcReports] = await Promise.all([
        supabase
          .from('sites')
          .select('id, name, address, customer_number, active')
          .eq('active', true)
          .order('name'),
        supabase
          .from('jobs')
          .select('site_id, scheduled_for, status')
          .in('status', ['completed', 'approved'])
          .order('scheduled_for', { ascending: false })
          .limit(2000),
        fetchQCReports(token),
      ]);

      if (sitesRes.error) throw sitesRes.error;
      if (jobsRes.error) throw jobsRes.error;

      const nextSites: SiteListing[] = (sitesRes.data ?? []).map((s) => ({
        id: s.id,
        name: s.name,
        address: s.address ?? '',
        customer_number: s.customer_number ?? null,
        active: Boolean(s.active),
      }));

      // most recent cleaning per site
      const seen = new Set<string>();
      const latest: SiteLastCleaning[] = [];
      for (const row of jobsRes.data ?? []) {
        if (seen.has(row.site_id)) continue;
        seen.add(row.site_id);
        latest.push({ siteId: row.site_id, scheduledFor: row.scheduled_for });
      }

      setSites(nextSites);
      setLastCleanings(latest);
      setReports(qcReports);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const lastQcBySite = useMemo(() => {
    const map = new Map<string, QCReportRow>();
    for (const r of reports) {
      const current = map.get(r.site_id);
      if (!current || r.inspected_at > current.inspected_at) {
        map.set(r.site_id, r);
      }
    }
    return map;
  }, [reports]);

  const lastCleaningBySite = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of lastCleanings) map.set(row.siteId, row.scheduledFor);
    return map;
  }, [lastCleanings]);

  const filteredSites = useMemo(() => {
    const query = search.trim().toLowerCase();
    const base = query.length === 0
      ? sites
      : sites.filter((site) => {
          const haystack = `${site.name} ${site.address} ${site.customer_number ?? ''}`.toLowerCase();
          return haystack.includes(query);
        });

    const sorted = [...base];
    sorted.sort((a, b) => {
      if (sortKey === 'name_asc') {
        return a.name.localeCompare(b.name, 'sv');
      }
      const aQc = lastQcBySite.get(a.id)?.inspected_at ?? null;
      const bQc = lastQcBySite.get(b.id)?.inspected_at ?? null;
      if (sortKey === 'recent_qc') {
        if (aQc && bQc) return bQc.localeCompare(aQc);
        if (aQc) return -1;
        if (bQc) return 1;
        return a.name.localeCompare(b.name, 'sv');
      }
      if (sortKey === 'oldest_qc') {
        if (aQc && bQc) return aQc.localeCompare(bQc);
        if (aQc) return -1;
        if (bQc) return 1;
        return a.name.localeCompare(b.name, 'sv');
      }
      // no_qc: never-inspected first, then alphabetical
      if (!aQc && bQc) return -1;
      if (aQc && !bQc) return 1;
      return a.name.localeCompare(b.name, 'sv');
    });
    return sorted;
  }, [sites, search, sortKey, lastQcBySite]);

  return (
    <div className="space-y-4 pb-6">
      <div className="cmd-panel cmd-panel-inner rounded p-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <span className="cmd-label">{text.title}</span>
          <div className="flex items-center gap-3">
            <span className="cmd-label text-[#8A6A47] font-mono tabular-nums">
              {filteredSites.length}/{sites.length} {text.objects}
            </span>
            <button
              type="button"
              onClick={() => void loadAll()}
              disabled={isLoading}
              className="cmd-label inline-flex items-center gap-1 rounded px-2 py-1 text-[#8A6A47] transition-colors hover:bg-[#073331]/[0.04] hover:text-[#073331] disabled:opacity-50"
              style={{ border: '1px solid rgba(7,51,49,0.12)' }}
            >
              {isLoading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={text.search}
            className="flex-1 min-w-[200px] rounded px-2 py-1.5 text-xs font-mono text-[#2E4640] placeholder:text-[#607C6E] focus:outline-none focus-visible:border-[#8A6A47]"
            style={{ background: '#FFFFFF', border: '1px solid rgba(7,51,49,0.12)' }}
          />
          <select
            value={sortKey}
            onChange={(event) => setSortKey(event.target.value as SortKey)}
            className="rounded px-2 py-1.5 text-xs font-mono text-[#2E4640] focus:outline-none focus-visible:border-[#8A6A47]"
            style={{ background: '#FFFFFF', border: '1px solid rgba(7,51,49,0.12)' }}
          >
            <option value="recent_qc">{text.sortRecent}</option>
            <option value="oldest_qc">{text.sortOldest}</option>
            <option value="no_qc">{text.sortNoQc}</option>
            <option value="name_asc">{text.sortName}</option>
          </select>
        </div>

        {error && (
          <div
            className="mb-3 rounded px-3 py-2 font-mono text-xs"
            style={{ border: '1px solid rgba(194,65,56,0.35)', background: 'rgba(194,65,56,0.06)', color: FAIL_INK }}
          >
            {error}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto cmd-scroll">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="cmd-label text-left py-1.5 pr-2 font-normal border-b border-[#073331]/10">{text.colBrf}</th>
                <th className="cmd-label text-left py-1.5 px-2 font-normal border-b border-[#073331]/10 w-32">{text.colLastQc}</th>
                <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10 w-16">{text.colScore}</th>
                <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10 w-32">{text.colLastCleaning}</th>
                <th className="border-b border-[#073331]/10 w-6" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="py-6 text-center font-mono text-xs text-[#607C6E]">
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {text.loading}
                    </span>
                  </td>
                </tr>
              ) : filteredSites.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-4 text-center font-mono text-xs text-[#607C6E]">
                    {text.empty}
                  </td>
                </tr>
              ) : (
                filteredSites.map((site) => {
                  const lastQc = lastQcBySite.get(site.id) ?? null;
                  const lastCleaning = lastCleaningBySite.get(site.id) ?? null;
                  return (
                    <tr
                      key={site.id}
                      onClick={() => onSelectSite(site.id)}
                      className="border-b border-[#073331]/10 cursor-pointer hover:bg-[#073331]/[0.04] transition-colors group align-middle"
                      style={{ height: 36 }}
                    >
                      <td className="py-1 pr-2">
                        <div className="font-mono text-xs text-[#2E4640] group-hover:text-[#073331] transition-colors leading-tight">
                          {site.name}
                        </div>
                        {(site.address || site.customer_number) ? (
                          <div className="font-mono text-[10px] text-[#607C6E] leading-tight truncate max-w-[260px]">
                            {site.address}
                            {site.customer_number ? `${site.address ? ' · ' : ''}#${site.customer_number}` : ''}
                          </div>
                        ) : null}
                      </td>
                      <td className="py-1 px-2">
                        {lastQc ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="font-mono tabular-nums text-xs text-[#073331]">
                              {formatDate(lastQc.inspected_at)}
                            </span>
                            {!lastQc.passed && (
                              <span
                                className="cmd-label w-fit px-1.5 py-0.5 rounded text-[8px]"
                                style={{ background: 'rgba(194,65,56,0.12)', color: FAIL_INK }}
                              >
                                {text.failed}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="font-mono text-xs text-[#607C6E]">{text.none}</span>
                        )}
                      </td>
                      <td className="py-1 px-2 text-right">
                        <ScoreNumeral score={lastQc ? lastQc.score : null} />
                      </td>
                      <td className="py-1 px-2 text-right font-mono tabular-nums text-xs text-[#073331]">
                        {lastCleaning ? (
                          formatDate(lastCleaning)
                        ) : (
                          <span className="text-[#607C6E]">{text.none}</span>
                        )}
                      </td>
                      <td className="py-1 pl-1 text-right">
                        <ChevronRight className="h-3.5 w-3.5 text-[#607C6E] group-hover:text-[#8A6A47] transition-colors inline-block" />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
